/**
 * MetaCompareBox — the box under a ranked row that says where the trainer
 * stands against it (2026-09-10 owner ask). Three states, three borders: red
 * for missing, yellow for the right Pokémon on the wrong (or unscanned)
 * moves, green for the right moves. The box is the way into that copy's
 * record page; the hub remembers where it was so Back lands here.
 *
 * LAID OUT LIKE THE ROW ABOVE IT. Same rank gutter, same 40px sprite, same
 * content column, so the copy's move pills sit directly under the ranked
 * move pills and the eye compares them without hunting (owner ask, second
 * pass). The standing sits at the right in the accent green, bold: the
 * league percent on PvP, the IV percent on raids.
 *
 * Colours are the app's own: destructive red, the warning ink, the accent
 * green. Grey "Missing" is centred so the empty box reads as a verdict, not a
 * broken row.
 */
import { CaughtSprite } from "@/components/CaughtSprite";
import { myPokemonDisplayName } from "@/lib/myPokemonFilter";
import { moveTypeFor } from "@/lib/moveTypeLookup";
import type { MetaCompare } from "@/lib/metaCompare";
import { MoveChip } from "./metaChrome";

const BORDER: Record<MetaCompare["state"], string> = {
  missing: "hsl(var(--destructive))",
  "wrong-moves": "var(--warning-text)",
  "right-moves": "var(--accent-fill)",
};

export function MetaCompareBox({
  compare,
  testId,
  onOpen,
  rankWidth = "w-8",
}: {
  compare: MetaCompare;
  testId: string;
  /** Open the copy's record page. Absent (missing) boxes render inert. */
  onOpen?: (recordId: string) => void;
  /** The row's rank-column width class, so the columns line up. */
  rankWidth?: "w-6" | "w-8";
}) {
  const border = BORDER[compare.state];
  const style = {
    background: "var(--surface-sunken)",
    border: `1.5px solid ${border}`,
  };

  if (compare.state === "missing" || !compare.record) {
    return (
      <div
        className="mt-1 w-full rounded-xl px-3 py-2 flex items-center justify-center"
        style={{ ...style, minHeight: 40 }}
        data-testid={testId}
        data-compare="missing"
      >
        <span className="text-[10.5px] font-bold uppercase tracking-wider" style={{ color: "var(--text-tertiary)" }}>
          Missing
        </span>
      </div>
    );
  }

  const record = compare.record;
  const movesColor = compare.state === "right-moves" ? "var(--accent-fill)" : "var(--warning-text)";

  return (
    <button
      type="button"
      onClick={() => onOpen?.(record.id)}
      className="mt-1 w-full rounded-xl px-3 py-2 flex items-center gap-3 text-left"
      style={style}
      data-testid={testId}
      data-compare={compare.state}
      aria-label={`Open ${myPokemonDisplayName(record)}`}
    >
      {/* The rank gutter, empty: it keeps the sprite under the row's sprite. */}
      <span className={`${rankWidth} shrink-0`} aria-hidden />

      {record.speciesId != null && (
        <CaughtSprite id={record.speciesId} shiny={record.isShiny} size={40} />
      )}

      <div className="min-w-0 flex-1">
        <p className="text-[12px] font-bold truncate" style={{ color: "var(--text-ink-bright)" }}>
          {myPokemonDisplayName(record)}
          {record.ivAtk != null ? (
            <span className="ml-1.5 text-[9.5px] font-semibold tabular-nums" style={{ color: "var(--text-secondary)" }}>
              {record.ivAtk}/{record.ivDef}/{record.ivHp}
            </span>
          ) : null}
        </p>
        {/* The copy's moves as the same pills the row wears, so the two lines
            read against each other. Unscanned says so in the verdict colour. */}
        <div className="mt-1 flex flex-wrap gap-1">
          {compare.have.length > 0 ? (
            compare.have.map((m) => <MoveChip key={m} name={m} type={moveTypeFor(m) ?? "normal"} />)
          ) : (
            <span className="text-[10px] font-semibold" style={{ color: movesColor }}>
              Moves not scanned
            </span>
          )}
        </div>
      </div>

      {compare.asForm && (
        // Judged as what it becomes: the destiny form's art says so.
        <span className="shrink-0" data-testid={`${testId}-as`}>
          <CaughtSprite id={compare.asForm.speciesId} shiny={record.isShiny} size={26} />
        </span>
      )}

      {compare.percent != null && (
        <span className="shrink-0 text-right" data-testid={`${testId}-percent`}>
          <span className="block text-[15px] font-black tabular-nums leading-none" style={{ color: "var(--accent-fill)" }}>
            {Math.round(compare.percent * 10) / 10}%
          </span>
          <span className="block text-[8px] font-bold uppercase tracking-wide mt-0.5" style={{ color: "var(--text-secondary)" }}>
            {compare.percentLabel}
          </span>
        </span>
      )}
    </button>
  );
}
